import { v4 as uuidv4 } from 'uuid'
import Big from 'big.js'
import store from 'state/store'
import {
  INVOICE_WEIGHT_MODES,
  ROUNDING_PRECISIONS,
  OUTBOUND_TICKET_STATUSES,
  INVOICE_STATUSES,
  MEASURE_UNIT,
} from 'utils/constants'
import { dispatchSnackbar } from 'utils/helpers'
import { getNetValue, convertPriceWeightUnits } from 'utils/data-manipulation/common'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { groupFinishedGoodsByMaterial } from 'services/finishedGoods'

export const initialInvoiceInformation = defaultCurrency => ({
  customer: {
    name: '',
    salesOrder: '',
  },
  details: {
    invoiceNo: '',
    paymentMethod: '',
    invoiceWeights: '',
    paymentTermId: '',
    currency: defaultCurrency,
    issueDate: new Date(),
    dateDue: new Date(),
    notes: '',
  },
  consumerWeights: [],
})

export const initialInvoiceSummaryRowData = {
  grossWeight: 0,
  tareWeight: 0,
  grossValue: 0,
  tareValue: 0,
  freightValue: 0,
  taxValue: 0,
  subTotal: 0,
  totalValue: 0,
}

export const initialInvoicePaymentData = {
  amountPaid: 0,
  remainingInvoiceBalance: 0,
  payFullAmount: false,
  paymentDate: null,
}

export const statusArray = [OUTBOUND_TICKET_STATUSES.INCOMPLETE, OUTBOUND_TICKET_STATUSES.SHIPPED]

export const invoiceStatusArray = [INVOICE_STATUSES.OPEN, INVOICE_STATUSES.CLOSED]

export const initialOutboundTicketInformation = {
  consumerInfo: {
    consumerName: '',
    dateCreated: '',
  },
  transportationInfo: {
    carrierLookup: '',
    driverName: '',
    truck: '',
    trailer: '',
    container: '',
    fob: '',
    seal: '',
    transportationType: '',
    export: false,
  },
  shippingInfo: {
    locationId: '',
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  },
  status: { value: statusArray[0].id },
  footerInformation: {
    freightCost: '',
    grossWeight: '',
    tareWeight: '',
    netWeight: '',
  },
  freightCost: {
    cost: 0,
    chargeBack: 0,
    userSet: false,
    userChargebackSet: false,
    id: '',
  },
}

export const initialLoadInformation = {
  loadWeight: {
    fgs: 0,
    gross: 0,
    tare: 0,
    net: 0,
  },
  truckWeight: {
    gross: 0,
    tare: 0,
    net: 0,
  },
}

export const findInvoiceStatusV2 = invoiceStatus => {
  const statusToFind = invoiceStatus?.value?.toUpperCase()
  if (!statusToFind) return null

  if (!INVOICE_STATUSES[statusToFind]) {
    dispatchSnackbar(store.dispatch, 'error', 'Invalid invoice status detected.')
    return
  }

  return INVOICE_STATUSES[statusToFind].name
}

export const calculateTotalInfoFields = materialRows => {
  let fgCount = 0
  let loadGross = 0
  let loadTare = 0
  let totalNet = 0

  materialRows.forEach(materialRow => {
    const { yardGross = 0, yardTare = 0, yardNet, fgId } = materialRow

    loadGross += yardGross
    loadTare += yardTare

    if (yardGross === 0) {
      totalNet += Number(yardNet) || 0
    } else {
      totalNet += yardGross - yardTare
    }

    if (fgId) {
      fgCount += 1
    }
  })

  return {
    fgCount,
    loadGross,
    loadTare,
    totalNet,
  }
}

// TODO split this up/refactor to make it cleaner
// Aggregates finished goods and materials, grouping them by material name and calculating totals.
export const aggregateFGsAndMaterials = (finishedGoods, materials) => {
  let totals = {
    gross: { value: 0 },
    tare: { value: 0 },
    net: { value: 0 },
    numPackages: { value: 0 },
    packageType: { value: null },
  }

  const groupedMaterials = groupFinishedGoodsByMaterial(finishedGoods)

  materials.forEach(material => {
    const materialEntry = groupedMaterials && groupedMaterials[material?.materialName]

    if (materialEntry) {
      groupedMaterials[material?.materialName] = [...materialEntry, material]
    } else {
      groupedMaterials[material?.materialName] = [material]
    }
  })

  const rows = Object.keys(groupedMaterials).map(material => {
    const groupedByMaterial = groupedMaterials[material]
    let subtotalGross = 0
    let subtotalTare = 0
    let subtotalNet = 0
    let subtotalNumPackages = 0
    let packageType = null

    groupedByMaterial.forEach(materialOrFG => {
      const isFG = !!materialOrFG?.fgId
      const totalsClone = { ...totals }

      totals = {
        ...totalsClone,
        gross: {
          value: totalsClone?.gross?.value + parseInt(materialOrFG?.gross, 10) || 0,
        },
        tare: {
          value: totalsClone?.tare?.value + parseInt(materialOrFG?.tare, 10) || 0,
        },
        net: {
          value: totalsClone?.net?.value + parseInt(materialOrFG?.net, 10) || 0,
        },
        numPackages: {
          value: totalsClone?.numPackages?.value + (isFG ? 1 : 0),
        },
      }

      subtotalGross += parseInt(materialOrFG?.gross, 10) || 0
      subtotalTare += parseInt(materialOrFG?.tare, 10) || 0
      subtotalNet += parseInt(materialOrFG?.net, 10) || 0
      subtotalNumPackages += isFG ? 1 : 0

      if (isFG) {
        packageType = materialOrFG?.type || materialOrFG?.fgType
      }
    })

    return {
      id: uuidv4(),
      materialName: material,
      numPackages: subtotalNumPackages,
      packageType,
      gross: subtotalGross,
      tare: subtotalTare,
      net: subtotalNet,
    }
  })

  return {
    rows,
    totals,
  }
}

/**
 * Calculates the row's net value based on the weightMode
 * If weightMode is INVOICE_WEIGHT_MODES.YARD, we first calculate the rounded line item totals before adding them together
 * Otherwise, we calculate the total using the consumer weights and consumer price
 * @param {object} params - The parameters for the calculation
 * @param {object} params.row - The row data containing weight and price information
 * @param {string} params.weightMode - The current weight mode for the invoice (YARD or CONSUMER)
 * @returns {number} The calculated net value for the row
 *
 * If weightMode is INVOICE_WEIGHT_MODES.YARD:
 * - Uses the yard price and net weights for calculation
 * - Calculates the total value by multiplying each net weight by the yard price
 * - Converts the yard price to price per pound if necessary
 *
 * If weightMode is INVOICE_WEIGHT_MODES.CONSUMER:
 * - Uses consumer weights and price for calculation
 * - Applies unit conversion if necessary
 *
 * The result is always rounded to the precision specified in ROUNDING_PRECISIONS.COST
 */
export const calculateRowNetValue = ({ row, weightMode, enablePullApartFeatures }) => {
  const { defaultYardUm } = store.getState()?.settings || {}

  const {
    allMaterialsNetArray = [],
    looseMaterialsNetArray = [],
    consumerGross = 0,
    consumerTare = 0,
    yardPrice = 0,
    consumerPrice = 0,
    consumerUm,
    yardUm,
    yardItemCount,
    consumerItemCount,
    deductions,
  } = row

  if (weightMode === INVOICE_WEIGHT_MODES.YARD) {
    let pricePerDefaultYardUm = new Big(yardPrice)

    if (enablePullApartFeatures && yardUm === MEASURE_UNIT.EACH && Number(yardItemCount) > 0) {
      // if yard is pull apart AND measure unit is each AND there is an item count, then:
      // 1. Get the count * price for ONLY the FGs
      // 2. Then, for ONLY the loose materials, get net * price for each row (because loose materials have no count)
      // 3. Return sum
      const fgTotal = new Big(yardItemCount).mul(pricePerDefaultYardUm).round(2, Big.roundHalfUp)
      const looseTotal = looseMaterialsNetArray.reduce((total, net) => {
        const lineItemValue = new Big(net).mul(pricePerDefaultYardUm).round(2, Big.roundHalfUp)
        return total.plus(lineItemValue)
      }, new Big(0))

      return fgTotal.plus(looseTotal).toNumber()
    }

    // If the price is not already in the defaultYardUm, convert it
    if (yardUm !== defaultYardUm) {
      pricePerDefaultYardUm = convertPriceWeightUnits(yardPrice, yardUm, defaultYardUm)
    }

    // First aggregate all materials net values
    const totalWeight = allMaterialsNetArray.reduce((total, net) => {
      return total.plus(new Big(net))
    }, new Big(0))

    const totalValue = totalWeight.mul(pricePerDefaultYardUm).round(2, Big.roundHalfUp)
    return totalValue.toNumber()
  }

  if (enablePullApartFeatures && consumerUm === MEASURE_UNIT.EACH && Number(consumerItemCount) > 0) {
    // if yard is pull apart AND consumer measure unit is each AND there is a consumer item count, then:
    // 1. Use consumerItemCount instead of the actual gross for getNetValue so that the calculation becomes count * price
    // 2. fromUnit and toUnit are both set as "EACH" so there is no automated price conversion
    return roundTo(
      getNetValue({
        gross: Number(consumerItemCount),
        tare: 0,
        price: parseFloat(consumerPrice) || 0,
        deductions,
        fromUnit: MEASURE_UNIT.EACH,
        toUnit: MEASURE_UNIT.EACH,
      }),
      ROUNDING_PRECISIONS.COST,
    )
  }
  return roundTo(
    getNetValue({
      gross: consumerGross,
      tare: consumerTare,
      price: parseFloat(consumerPrice) || 0,
      deductions,
      fromUnit: defaultYardUm,
      toUnit: consumerUm || defaultYardUm,
    }),
    ROUNDING_PRECISIONS.COST,
  )
}

export const formatPullApartScrapRows = (savedPullApartScrapRows = [], activePullApartScrapRows = []) => {
  const activeRowsDict = {}
  activePullApartScrapRows.forEach(activeRow => {
    activeRowsDict[activeRow.row] = activeRow.numberOfVins
  })

  return savedPullApartScrapRows
    .filter(savedRow => !!activeRowsDict[savedRow.row])
    .map(savedRow => ({ ...savedRow, numberOfVins: activeRowsDict[savedRow.row] }))
}
